import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Toast, { ToastProps, ToastVariant } from '#shared/components/Toast/Toast';
import { ToastContext, ToastMethod, ToastOptions, ToastService } from './ToastContext';

// Shared owns the toast UI (adapted from crossborder-iframe's Toast.provider). One toast at a time:
// raising a new one while another is showing dismisses the current one first, then opens the new one
// after a short gap so the enter animation plays again.
export default function ToastProvider({ children }: { children: ReactNode }) {
  const reopenTimerRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);
  const [toastProps, setToastProps] = useState<Omit<ToastProps, 'isOpen'>>({ onOpenChange: setIsOpen });

  useEffect(() => () => window.clearTimeout(reopenTimerRef.current), []);

  function createMethod(variant: ToastVariant): ToastMethod {
    return (title, options: ToastOptions = {}) => {
      setIsOpen(false);
      window.clearTimeout(reopenTimerRef.current);

      reopenTimerRef.current = window.setTimeout(() => {
        setToastProps({ title, variant, onOpenChange: setIsOpen, ...options });
        setIsOpen(true);
      }, 100);
    };
  }

  // Built once: the methods only close over state setters and the timer ref, all stable, so consumers
  // get one service identity for the provider's lifetime (see AlertDialog.provider).
  const service = useMemo<ToastService>(
    () => ({
      success: createMethod('success'),
      neutral: createMethod('neutral'),
      error: createMethod('error'),
      close: () => setIsOpen(false),
    }),
    [],
  );

  return (
    <Toast.Provider swipeDirection="right" swipeThreshold={150} duration={2500}>
      <ToastContext.Provider value={service}>
        {children}
        <Toast {...toastProps} isOpen={isOpen} />
        <Toast.Viewport />
      </ToastContext.Provider>
    </Toast.Provider>
  );
}
